/**
 * Report host.
 *
 * Drill-down is the point: Balance Sheet -> ledger -> statement ->
 * voucher -> reverse. Clicking a number never dead-ends, which is what
 * makes it feel like Tally rather than a set of printouts.
 */
import { useCallback } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Alert } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { P } from '../api/types'
import { useAuth } from '../store/AuthContext'
import DayBook from '../reports/DayBook'
import LedgerStatement from '../reports/LedgerStatement'
import TrialBalance from '../reports/TrialBalance'
import BalanceSheet from '../reports/BalanceSheet'
import ProfitLoss from '../reports/ProfitLoss'
import OutstandingScreen from '../reports/Outstanding'
import Register from '../reports/Register'
import CashBook from '../reports/CashBook'
import VatReturn from '../reports/VatReturn'

type Which = 'daybook' | 'ledger' | 'trial-balance' | 'balance-sheet'
  | 'profit-loss' | 'outstanding' | 'sales-register' | 'purchase-register'
  | 'cash-book' | 'bank-book' | 'vat-return'

const NEEDS: Record<Which, string> = {
  daybook: P.reportOperational,
  ledger: P.reportOperational,
  'trial-balance': P.reportFinancial,
  'balance-sheet': P.reportFinancial,
  'profit-loss': P.reportFinancial,
  outstanding: P.reportOperational,
  // Operational, not financial: a register lists invoices and their VAT.
  // It shows no margin, so the clerk who raised them may read it.
  'sales-register': P.reportOperational,
  'purchase-register': P.reportOperational,
  'cash-book': P.reportOperational,
  'bank-book': P.reportOperational,
  // The return is the company's tax position: financial.
  'vat-return': P.reportFinancial,
}

export default function ReportsPage({ which }: { which: Which }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const nav = useNavigate()
  const [params] = useSearchParams()

  // Every drill-down lands on the ledger statement with ?code=...
  const openLedger = useCallback((code: string) => {
    nav(`/ledger?code=${encodeURIComponent(code)}`)
  }, [nav])

  if (!can(NEEDS[which])) {
    return <Alert severity="warning">{t('report.noPermission')}</Alert>
  }

  switch (which) {
    case 'daybook':
      return <DayBook onLedger={openLedger} />
    case 'ledger':
      return <LedgerStatement code={params.get('code') ?? undefined}
        onCodeChange={(c) => nav(`/ledger?code=${encodeURIComponent(c)}`,
          { replace: true })} />
    case 'trial-balance':
      return <TrialBalance onLedger={openLedger} />
    case 'balance-sheet':
      return <BalanceSheet onLedger={openLedger} />
    case 'profit-loss':
      return <ProfitLoss onLedger={openLedger} />
    case 'outstanding':
      return <OutstandingScreen onLedger={openLedger} />
    case 'sales-register':
      return <Register kind="sales" />
    case 'purchase-register':
      return <Register kind="purchase" />
    case 'cash-book':
      return <CashBook kind="cash" />
    case 'bank-book':
      return <CashBook kind="bank" />
    case 'vat-return':
      return <VatReturn />
    default:
      return <Navigate to="/" replace />
  }
}
